// California meal-period premiums (Labor Code §512/§226.7), built exactly as legacy computes them (parity): the api's
// `WorkdayMealPremiumAggregator` (chains an employee's cards into work periods) and `MealPremiumDetector::detectPeriod`
// (judges each period's first and second meal), as the payroll report runs them (`PremiumPayrollService` with the
// cross-day window). Advisory only: it never pays anything by itself and is recomputed on read, never stored. Legacy
// reads it only when the location turns on "Flag potential meal-period premiums" (`ca_meal_premium_flagging_enabled`).
//
// The rule, in legacy's order:
//   1. Cards (closed, ordered by clock-in) chain into one work period while the gap between one card's clock-out and the
//      next one's clock-in stays under 8 hours, across the business-day start too. A gap of 30 minutes or more inside a
//      period is assumed to be a meal (and flagged for a manager to confirm: routine up to 60 minutes, split-shift-like
//      above), but it only counts as a meal when it starts with at most 5 worked hours behind it.
//   2. A meal is an unpaid break (not the excess overrun of a paid one) that lasted at least 30 minutes. Worked time is
//      the clocked span minus those meals. A shorter or still-open unpaid break is "unconfirmable".
//   3. Above 5 worked hours a first meal is due, above 10 a second; each is due by the instant worked time reaches
//      5 h / 10 h (walking the cards and skipping meals and the gaps between cards).
//   4. Meals are claimed, never shared: first each due meal takes the earliest unclaimed meal that began by its
//      deadline; then each still-unmet meal is waived (a waiver on file and worked time at most 6 h / 12 h), else owed
//      late (an unclaimed later meal), else needs review (an open break), else owed short (a short break), else owed.
//   5. Each owed meal counts toward the business day its deadline falls in; one premium (one hour of the base wage) per
//      business day. A wage under $7.25 is treated as missing: the day needs review and no amount is computed.
// Labor-law sign-off on these rules waits on asks A16; until then they are legacy's, unchanged.
import type { Money } from '@radish/primitives';
import type { Instant } from '../aggregate/context.js';
import { add, zero } from '../money/index.js';
import { businessDay, type BusinessDate, type BusinessDayLocation } from '../time/index.js';
import type { TimecardBreak, TimecardId } from './timecard/types.js';
import { DEFAULT_DETAIL_ARITHMETIC, type DetailArithmetic } from './employee-detail-legacy.js';

/** Bumped whenever the same inputs would produce a different premium. */
/** 2: a break whose definition was deleted is ignored, as legacy does (B418). */
export const MEAL_PREMIUM_RULES_VERSION = 2;

const MINUTE = 60_000n;
/** Legacy `QUALIFYING_MEAL_MINUTES`: an unpaid break this long is a bona-fide meal. */
const QUALIFYING_MEAL = 30n * MINUTE;
/** Legacy `ASSUMED_MEAL_GAP_MINUTES` and `SPLIT_SHIFT_GAP_MINUTES`. */
const ASSUMED_MEAL_GAP = 30n * MINUTE;
const SPLIT_SHIFT_GAP = 60n * MINUTE;
/** Legacy `WORK_PERIOD_SPLIT_GAP_MINUTES`: a gap this long starts a new work period. */
export const WORK_PERIOD_SPLIT_GAP_MINUTES = 480;
const WORK_PERIOD_SPLIT_GAP = BigInt(WORK_PERIOD_SPLIT_GAP_MINUTES) * MINUTE;
const FIRST_MEAL_AFTER = 300n * MINUTE;
const SECOND_MEAL_AFTER = 600n * MINUTE;
/** A waiver covers a meal only up to these worked hours (6 h first, 12 h second). */
const FIRST_WAIVER_CEILING = 360n * MINUTE;
const SECOND_WAIVER_CEILING = 720n * MINUTE;
/** Legacy `MINIMUM_VALID_WAGE_CENTS`: the federal minimum, $7.25 an hour; a lower wage is treated as missing. */
export const MINIMUM_VALID_WAGE_MINOR = 725n;
/** Legacy `PremiumPayrollService::PREMIUM_WINDOW_DAYS`: payroll reads cards this many days past each end of its range. */
export const MEAL_PREMIUM_WINDOW_DAYS = 3;

/** Legacy waiver keys (`ca_meal_first`, `ca_meal_second`), as the employee's waivers on file. */
export type MealWaiver = 'ca_meal_first' | 'ca_meal_second';

export type MealPremiumCard = {
  readonly timecardId: TimecardId;
  readonly clockedInAt: Instant;
  readonly clockedOutAt: Instant;
  /** `definitionDeleted`: the break's definition was deleted (retired) since; legacy then ignores it (B418). */
  readonly breaks: readonly (TimecardBreak & { readonly definitionDeleted?: boolean })[];
};

/** One meal's verdict: `owed_short` and `owed_late` are rebuttable presumptions, `owed` a clean miss. */
export type MealVerdictState = 'clear' | 'waived' | 'review' | 'owed' | 'owed_short' | 'owed_late';

export type MealVerdict = {
  /** 1 for the first meal, 2 for the second. */
  readonly meal: 1 | 2;
  readonly state: MealVerdictState;
  readonly deadlineAt: Instant;
  /** When the meal (or the short break) that settled it began. */
  readonly takenAt?: Instant;
  /** The business day an owed meal counts toward (its deadline's). */
  readonly workday?: BusinessDate;
  /** A clean first-meal miss whose only meal was counted toward the second meal (legacy copy names it). */
  readonly countedTowardSecond?: boolean;
  /** A clean miss with a waiver on file that is void above the hour ceiling (legacy copy names it). */
  readonly waiverVoid?: boolean;
};

export type MealPremiumState = 'owed' | 'owed_late' | 'review' | 'clear';

export type MealAdvisory = 'confirm_bona_fide_meal_routine' | 'confirm_bona_fide_meal_split_shift' | 'tracking_incomplete_may_owe_one';

/** The capped premium for one business day of one employee (legacy `WorkdayMealPremiumResult`). */
export type WorkdayMealPremium = {
  readonly businessDate: BusinessDate;
  readonly state: MealPremiumState;
  /** One hour of the base wage on an owed day; zero otherwise, and zero with an invalid wage (the day needs review). */
  readonly premium: Money;
  readonly timecardIds: readonly TimecardId[];
  readonly meals: readonly MealVerdict[];
  readonly advisories: readonly MealAdvisory[];
  /** More than one business day of the shift owes a premium. */
  readonly spansBusinessDays: boolean;
  /** The work period crosses the business-day start (and has something owed or to review). */
  readonly crossesBusinessDay: boolean;
  /** Owed, but the wage is missing or under the minimum: no amount can be computed. */
  readonly invalidWage: boolean;
  /**
   * The period's chain reached an edge of the loaded window and a card beyond it would have chained (legacy
   * `window_saturated`): the verdict may be incomplete. Always false without a `window`.
   */
  readonly windowSaturated: boolean;
};

export type MealPremiumAssessment = {
  /** Every business day with something owed or to review (and clean days with `includeClearDays`), in date order. */
  readonly days: readonly WorkdayMealPremium[];
  /** Business days carrying an owed meal, whatever the wage. */
  readonly owedDays: readonly BusinessDate[];
  /** The sum of the owed days' premiums; null when something is owed but the wage is invalid. */
  readonly premium: Money | null;
};

export type MealPremiumInput = {
  /** The employee's closed cards at the location; any order. */
  readonly cards: readonly MealPremiumCard[];
  /** The employee's hourly base wage (legacy `employees.wage`). */
  readonly wage: Money;
  readonly waivers: readonly MealWaiver[];
  /** An employer-set exemption (legacy `employee_meal_premium_exemptions`): nothing is evaluated. */
  readonly exempt: boolean;
  readonly location: BusinessDayLocation;
  /** Also list evaluated days with nothing to flag (legacy `emitCleanDays`). */
  readonly includeClearDays?: boolean;
  /**
   * The window `cards` were loaded from, and the employee's cards just beyond it (legacy `saturationWindow`, the
   * timecards list): a period near an edge that such a card would have chained into is marked `windowSaturated`.
   */
  readonly window?: MealPremiumWindow;
  /**
   * The correction of B418, switched off until parity is validated (ledger L579): count a break whose definition was
   * deleted as the kind it was taken as. Off, such a break is neither a meal nor unconfirmable, as legacy's detector reads
   * its missing definition (`break?->paid === false`).
   */
  readonly countDeletedDefinitions?: boolean;
};

export type MealPremiumWindow = {
  readonly start: Instant;
  readonly end: Instant;
  /** Cards clocked in before `start` or after `end` (open ones have no clock-out). */
  readonly outside: readonly { readonly clockedInAt: Instant; readonly clockedOutAt?: Instant }[];
};

type Interval = { readonly start: Instant; readonly end: Instant };
type Period = {
  readonly businessDate: BusinessDate;
  readonly start: Instant;
  readonly end: Instant;
  readonly timecardIds: readonly TimecardId[];
  readonly worked: bigint;
  readonly meals: readonly Interval[];
  readonly unconfirmable: readonly { readonly start: Instant; readonly open: boolean }[];
  readonly segments: readonly Interval[];
  readonly advisories: readonly MealAdvisory[];
};

const abs = (n: bigint): bigint => (n < 0n ? -n : n);
const byStart = <T extends { readonly start: Instant }>(a: T, b: T): number => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0);
const unique = <T>(items: readonly T[]): T[] => [...new Set(items)];

/** A card's meals: unpaid (not excess) breaks that ended and lasted at least 30 minutes. */
function cardMeals(card: MealPremiumCard): Interval[] {
  return card.breaks
    .filter((b) => b.kind === 'unpaid' && b.endedAt !== undefined && abs(b.endedAt - b.startedAt) >= QUALIFYING_MEAL)
    .map((b) => ({ start: b.startedAt, end: b.endedAt as Instant }));
}

/** A card's unpaid breaks that are not meals: still open, or closed under 30 minutes. */
function cardUnconfirmable(card: MealPremiumCard): { start: Instant; open: boolean }[] {
  return card.breaks
    .filter((b) => b.kind === 'unpaid' && (b.endedAt === undefined || abs(b.endedAt - b.startedAt) < QUALIFYING_MEAL))
    .map((b) => ({ start: b.startedAt, open: b.endedAt === undefined }));
}

/** Legacy `chainContiguousCards`: cards ordered by clock-in, split where a gap reaches 8 hours. */
function chains(cards: readonly MealPremiumCard[]): MealPremiumCard[][] {
  const ordered = [...cards].sort((a, b) =>
    a.clockedInAt !== b.clockedInAt ? (a.clockedInAt < b.clockedInAt ? -1 : 1) : a.timecardId < b.timecardId ? -1 : a.timecardId > b.timecardId ? 1 : 0,
  );
  const out: MealPremiumCard[][] = [];
  let current: MealPremiumCard[] = [];
  let previousOut: Instant | undefined;
  for (const card of ordered) {
    if (previousOut !== undefined && abs(card.clockedInAt - previousOut) >= WORK_PERIOD_SPLIT_GAP) {
      out.push(current);
      current = [];
    }
    current.push(card);
    previousOut = card.clockedOutAt;
  }
  if (current.length > 0) out.push(current);
  return out;
}

/** Legacy `assembleOne`: one chain as a work period, its between-card gaps judged as assumed meals. */
function assemble(chain: readonly MealPremiumCard[], location: BusinessDayLocation): Period {
  const first = chain[0] as MealPremiumCard;
  const last = chain[chain.length - 1] as MealPremiumCard;
  const meals: Interval[] = [];
  const unconfirmable: { start: Instant; open: boolean }[] = [];
  const advisories: MealAdvisory[] = [];
  let worked = 0n;
  let previousOut: Instant | undefined;
  for (const card of chain) {
    if (previousOut !== undefined) {
      const gap = abs(card.clockedInAt - previousOut);
      if (gap >= ASSUMED_MEAL_GAP) {
        // The gap is the meal only when it begins within the first 5 worked hours; the confirm flag is raised either way.
        if (worked <= FIRST_MEAL_AFTER) meals.push({ start: previousOut, end: card.clockedInAt });
        advisories.push(gap > SPLIT_SHIFT_GAP ? 'confirm_bona_fide_meal_split_shift' : 'confirm_bona_fide_meal_routine');
      }
    }
    const own = cardMeals(card);
    meals.push(...own);
    unconfirmable.push(...cardUnconfirmable(card));
    const span = abs(card.clockedOutAt - card.clockedInAt);
    const mealTime = own.reduce((n, m) => n + abs(m.end - m.start), 0n);
    worked += span > mealTime ? span - mealTime : 0n;
    previousOut = card.clockedOutAt;
  }
  return {
    businessDate: businessDay(first.clockedInAt, location),
    start: first.clockedInAt,
    end: last.clockedOutAt,
    timecardIds: chain.map((c) => c.timecardId),
    worked,
    meals: meals.sort(byStart),
    unconfirmable: unconfirmable.sort(byStart),
    segments: chain.map((c) => ({ start: c.clockedInAt, end: c.clockedOutAt })),
    advisories: unique(advisories),
  };
}

/** Legacy `workedRuns`: each card's span with its meals carved out. */
function workedRuns(period: Period): Interval[] {
  const runs: Interval[] = [];
  for (const seg of period.segments) {
    let cursor = seg.start;
    for (const meal of period.meals) {
      if (meal.start < seg.start || meal.start >= seg.end) continue;
      if (meal.start > cursor) runs.push({ start: cursor, end: meal.start });
      if (meal.end > cursor) cursor = meal.end;
    }
    if (cursor < seg.end) runs.push({ start: cursor, end: seg.end });
  }
  return runs;
}

/**
 * Legacy `workedMinuteInstant`: the instant worked time reaches `target`, the remaining minutes rounded half-up to whole
 * minutes (`addMinutes((int) round(...))`); the period's end when it never does.
 */
function deadline(period: Period, runs: readonly Interval[], target: bigint): Instant {
  let accumulated = 0n;
  for (const run of runs) {
    const length = abs(run.end - run.start);
    if (accumulated + length >= target) return run.start + ((target - accumulated + MINUTE / 2n) / MINUTE) * MINUTE;
    accumulated += length;
  }
  return period.end;
}

type Axis = { readonly meal: 1 | 2; readonly due: boolean; readonly deadlineAt: Instant; readonly waiver: boolean; readonly ceiling: bigint };

/** Legacy `evaluateMeals`: consuming assignment of the period's meals and breaks to the first and second meal. */
function judgeMeals(period: Period, waivers: readonly MealWaiver[], location: BusinessDayLocation, anchoredAtClockIn = false): MealVerdict[] {
  const runs = period.worked > FIRST_MEAL_AFTER && !anchoredAtClockIn ? workedRuns(period) : [];
  // The per-card read (legacy `detect`) anchors the deadlines at clock-in + 5 h / 10 h; a work period walks worked time.
  const due = (target: bigint): Instant => (anchoredAtClockIn ? period.start + target : period.worked > target ? deadline(period, runs, target) : period.end);
  const axes: Axis[] = [
    {
      meal: 1, due: period.worked > FIRST_MEAL_AFTER, waiver: waivers.includes('ca_meal_first'), ceiling: FIRST_WAIVER_CEILING,
      deadlineAt: due(FIRST_MEAL_AFTER),
    },
    {
      meal: 2, due: period.worked > SECOND_MEAL_AFTER, waiver: waivers.includes('ca_meal_second'), ceiling: SECOND_WAIVER_CEILING,
      deadlineAt: due(SECOND_MEAL_AFTER),
    },
  ];
  const mealTaken = period.meals.map(() => false);
  const breakTaken = period.unconfirmable.map(() => false);
  const verdicts = new Map<1 | 2, MealVerdict>();
  const owedOn = (axis: Axis): BusinessDate => businessDay(axis.deadlineAt, location);

  // Pass A: in-window claims settle first, the first meal then the second.
  for (const axis of axes) {
    if (!axis.due) continue;
    const i = period.meals.findIndex((m, j) => !mealTaken[j] && m.start <= axis.deadlineAt);
    if (i < 0) continue;
    mealTaken[i] = true;
    verdicts.set(axis.meal, { meal: axis.meal, state: 'clear', deadlineAt: axis.deadlineAt, takenAt: (period.meals[i] as Interval).start });
  }
  // Pass B: each unmet meal, in order: waived, late, open break (review), short break, owed.
  for (const axis of axes) {
    if (!axis.due || verdicts.has(axis.meal)) continue;
    if (axis.waiver && period.worked <= axis.ceiling) {
      verdicts.set(axis.meal, { meal: axis.meal, state: 'waived', deadlineAt: axis.deadlineAt });
      continue;
    }
    const late = mealTaken.findIndex((taken) => !taken);
    if (late >= 0) {
      mealTaken[late] = true;
      verdicts.set(axis.meal, { meal: axis.meal, state: 'owed_late', deadlineAt: axis.deadlineAt, takenAt: (period.meals[late] as Interval).start, workday: owedOn(axis) });
      continue;
    }
    const open = period.unconfirmable.findIndex((b, j) => !breakTaken[j] && b.open);
    if (open >= 0) {
      breakTaken[open] = true;
      verdicts.set(axis.meal, { meal: axis.meal, state: 'review', deadlineAt: axis.deadlineAt });
      continue;
    }
    const short = period.unconfirmable.findIndex((b, j) => !breakTaken[j] && !b.open);
    if (short >= 0) {
      breakTaken[short] = true;
      const taken = period.unconfirmable[short] as { start: Instant };
      verdicts.set(axis.meal, { meal: axis.meal, state: 'owed_short', deadlineAt: axis.deadlineAt, takenAt: taken.start, workday: owedOn(axis) });
      continue;
    }
    verdicts.set(axis.meal, {
      meal: axis.meal, state: 'owed', deadlineAt: axis.deadlineAt, workday: owedOn(axis),
      ...(axis.meal === 1 && mealTaken.includes(true) ? { countedTowardSecond: true } : axis.waiver ? { waiverVoid: true } : {}),
    });
  }
  return [verdicts.get(1), verdicts.get(2)].filter((v): v is MealVerdict => v !== undefined);
}

type PeriodResult = {
  readonly state: MealPremiumState;
  readonly owedDays: readonly BusinessDate[];
  readonly meals: readonly MealVerdict[];
  readonly crossesBusinessDay: boolean;
};

const OWED: readonly MealVerdictState[] = ['owed', 'owed_short', 'owed_late'];

/** Legacy `buildResult`: the period's state; a clean or short miss outranks a late one, and no wage means review. */
function periodResult(period: Period, meals: readonly MealVerdict[], wage: Money, location: BusinessDayLocation): PeriodResult {
  const owed = meals.filter((m) => OWED.includes(m.state));
  const owedDays = unique(owed.map((m) => m.workday as BusinessDate));
  const review = meals.some((m) => m.state === 'review');
  const crossesBusinessDay = (owedDays.length > 0 || review) && businessDay(period.start, location) !== businessDay(period.end, location);
  if (owedDays.length > 0) {
    const confident = owed.some((m) => m.state !== 'owed_late');
    return { state: wage.minor > 0n ? (confident ? 'owed' : 'owed_late') : 'review', owedDays, meals, crossesBusinessDay };
  }
  return { state: review ? 'review' : 'clear', owedDays: [], meals, crossesBusinessDay };
}

const sameVerdict = (a: MealVerdict, b: MealVerdict): boolean =>
  a.meal === b.meal && a.state === b.state && a.deadlineAt === b.deadlineAt && a.takenAt === b.takenAt && a.workday === b.workday;
const mergeMeals = (a: readonly MealVerdict[], b: readonly MealVerdict[]): MealVerdict[] =>
  [...a, ...b].filter((v, i, all) => all.findIndex((w) => sameVerdict(v, w)) === i);

/**
 * Legacy `windowSaturated`: the period starts within 8 hours of the window start and a closed card clocked in before the
 * window ends within 8 hours of the period's start; or the period ends less than 8 hours before the window end (or past
 * it) and a card clocks in after the window end within 8 hours of the period's end.
 */
function saturated(period: Period, window: MealPremiumWindow | undefined): boolean {
  if (window === undefined) return false;
  const nearLeft = abs(period.start - window.start) < WORK_PERIOD_SPLIT_GAP;
  const nearRight = period.end + WORK_PERIOD_SPLIT_GAP > window.end;
  const beforeChains = window.outside.some((c) => c.clockedOutAt !== undefined && c.clockedInAt < window.start && c.clockedOutAt > period.start - WORK_PERIOD_SPLIT_GAP);
  const afterChains = window.outside.some((c) => c.clockedInAt > window.end && c.clockedInAt < period.end + WORK_PERIOD_SPLIT_GAP);
  return (nearLeft && beforeChains) || (nearRight && afterChains);
}

/** Legacy `assessMealPremiums` = the aggregator's `aggregateFromTimecards` + `rollUp`, one call per employee. */
/** Legacy's view of a card's breaks: one whose definition was deleted drops out (excess breaks have none to lose). */
const asLegacySees = (card: MealPremiumCard, countDeleted: boolean | undefined): MealPremiumCard =>
  countDeleted === true || !card.breaks.some((b) => b.definitionDeleted === true)
    ? card
    : { ...card, breaks: card.breaks.filter((b) => b.kind === 'excess' || b.definitionDeleted !== true) };

export function assessMealPremiums(raw: MealPremiumInput): MealPremiumAssessment {
  const input = { ...raw, cards: raw.cards.map((c) => asLegacySees(c, raw.countDeletedDefinitions)) };
  const { wage, location } = input;
  if (input.exempt || input.cards.length === 0) return { days: [], owedDays: [], premium: null };
  const validWage = wage.minor >= MINIMUM_VALID_WAGE_MINOR;
  const byDay = new Map<BusinessDate, WorkdayMealPremium>();
  const owedDays: BusinessDate[] = [];
  for (const chain of chains(input.cards)) {
    const period = assemble(chain, location);
    const result = periodResult(period, judgeMeals(period, input.waivers, location), wage, location);
    const windowSaturated = saturated(period, input.window);
    if (result.owedDays.length > 0) {
      const spans = result.owedDays.length > 1;
      for (const day of result.owedDays) {
        owedDays.push(day);
        const existing = byDay.get(day);
        const advisories = [...period.advisories, ...(spans ? (['tracking_incomplete_may_owe_one'] as const) : [])];
        // One premium per business day: a second period owed on the same day merges in without adding another.
        byDay.set(day, {
          businessDate: day,
          state: validWage ? result.state : 'review',
          premium: validWage ? wage : zero(wage.currency),
          timecardIds: unique([...(existing?.timecardIds ?? []), ...period.timecardIds]),
          meals: mergeMeals(existing?.meals ?? [], result.meals),
          advisories: unique([...(existing?.advisories ?? []), ...advisories]),
          spansBusinessDays: spans || (existing?.spansBusinessDays ?? false),
          crossesBusinessDay: result.crossesBusinessDay || (existing?.crossesBusinessDay ?? false),
          invalidWage: !validWage,
          windowSaturated: windowSaturated || (existing?.windowSaturated ?? false),
        });
      }
      continue;
    }
    const needsReview = period.advisories.length > 0 || result.state === 'review';
    if (!needsReview && input.includeClearDays !== true) continue;
    const existing = byDay.get(period.businessDate);
    // An owed day outranks anything else landing on it.
    if (existing !== undefined && (existing.state === 'owed' || existing.state === 'owed_late')) continue;
    const row: WorkdayMealPremium = {
      businessDate: period.businessDate,
      state: needsReview ? 'review' : 'clear',
      premium: zero(wage.currency),
      timecardIds: period.timecardIds,
      meals: result.meals,
      advisories: needsReview ? period.advisories : [],
      spansBusinessDays: false,
      crossesBusinessDay: result.crossesBusinessDay,
      invalidWage: false,
      windowSaturated,
    };
    byDay.set(period.businessDate, existing === undefined ? row : {
      ...row,
      state: existing.state === 'review' || row.state === 'review' ? 'review' : 'clear',
      timecardIds: unique([...existing.timecardIds, ...row.timecardIds]),
      meals: mergeMeals(existing.meals, row.meals),
      advisories: unique([...existing.advisories, ...row.advisories]),
      spansBusinessDays: existing.spansBusinessDays,
      crossesBusinessDay: existing.crossesBusinessDay || row.crossesBusinessDay,
      invalidWage: existing.invalidWage,
      windowSaturated: existing.windowSaturated || row.windowSaturated,
    });
  }
  const days = [...byDay.values()].sort((a, b) => (a.businessDate < b.businessDate ? -1 : 1));
  const owed = unique(owedDays).sort();
  const premium = validWage && owed.length > 0
    ? days.filter((d) => owed.includes(d.businessDate)).reduce((total, d) => add(total, d.premium), zero(wage.currency))
    : null;
  return { days, owedDays: owed, premium };
}

/**
 * The payroll report's Meal Premium for a pay period (legacy `PremiumPayrollService::injectPremium` with the cross-day
 * window): the assessment ran over cards from 3 days either side of the range, and only owed days inside the range are
 * paid here. Zero when nothing is owed in the range, null when something is but the wage is invalid. `days` are the
 * owed days (in range or not); one outside the range is paid in the period it falls in.
 */
export function payrollMealPremium(
  assessment: MealPremiumAssessment,
  from: BusinessDate,
  to: BusinessDate,
  currency: Money['currency'],
): { readonly premium: Money | null; readonly days: readonly (WorkdayMealPremium & { readonly outsideRange: boolean })[] } {
  const inRange = (d: BusinessDate): boolean => d >= from && d <= to;
  const owedRows = assessment.days.filter((d) => d.state === 'owed' || d.state === 'owed_late');
  const days = owedRows.map((d) => ({ ...d, outsideRange: !inRange(d.businessDate) }));
  if (!assessment.owedDays.some(inRange)) return { premium: zero(currency), days };
  if (assessment.premium === null) return { premium: null, days };
  return { premium: owedRows.filter((d) => inRange(d.businessDate)).reduce((t, d) => add(t, d.premium), zero(currency)), days };
}


/**
 * The Meal premiums report's row for one employee (legacy `MealPremiumReportService::reportForEmployee`): the flagged
 * days inside the range, how many of them are owed (whatever the wage), and their premiums' sum. The sum is null when
 * nothing in the range is owed or the wage is invalid (never $0.00); an employee with no day listed is left off.
 */
export function reportMealPremium(
  assessment: MealPremiumAssessment,
  from: BusinessDate,
  to: BusinessDate,
  currency: Money['currency'],
): { readonly days: readonly WorkdayMealPremium[]; readonly owedDays: number; readonly premium: Money | null } {
  const inRange = (d: BusinessDate): boolean => d >= from && d <= to;
  const days = assessment.days.filter((d) => d.state !== 'clear' && inRange(d.businessDate));
  const owed = assessment.owedDays.filter(inRange);
  if (days.length === 0) return { days: [], owedDays: 0, premium: null };
  const premium = owed.length === 0 || assessment.premium === null
    ? null
    : days.filter((d) => owed.includes(d.businessDate)).reduce((t, d) => add(t, d.premium), zero(currency));
  return { days, owedDays: owed.length, premium };
}

/**
 * Whether one card on its own trips a meal violation (legacy `MealPremiumDetector::detect`, the timecards list's
 * `causes_meal_violation`): the card alone, its deadlines at clock-in + 5 h / 10 h, owed or owed late with a wage on file.
 * A card that only owes in combination with others is "part of a series" instead.
 */
export function cardCausesMealViolation(raw: MealPremiumCard, input: Omit<MealPremiumInput, 'cards' | 'includeClearDays'>): boolean {
  if (input.exempt) return false;
  const card = asLegacySees(raw, input.countDeletedDefinitions);
  const own = cardMeals(card);
  const span = abs(card.clockedOutAt - card.clockedInAt);
  const mealTime = own.reduce((n, m) => n + abs(m.end - m.start), 0n);
  const period: Period = {
    businessDate: businessDay(card.clockedInAt, input.location),
    start: card.clockedInAt,
    end: card.clockedOutAt,
    timecardIds: [card.timecardId],
    worked: span > mealTime ? span - mealTime : 0n,
    meals: own.sort(byStart),
    unconfirmable: cardUnconfirmable(card).sort(byStart),
    segments: [{ start: card.clockedInAt, end: card.clockedOutAt }],
    advisories: [],
  };
  const state = periodResult(period, judgeMeals(period, input.waivers, input.location, true), input.wage, input.location).state;
  return state === 'owed' || state === 'owed_late';
}

/** The marker a closed card carries on the timecards list, most urgent first (legacy `TimecardMealPremiumChip` tiers). */
export type TimecardMealChip =
  /** Its chain hit the edge of the loaded window: the verdict may be incomplete ("Can't evaluate"). */
  | { readonly chip: 'cant_evaluate'; readonly businessDate: BusinessDate }
  /** It trips a violation on its own ("Non-compliant meal"). */
  | { readonly chip: 'causes_violation' }
  /** It owes only together with other cards ("Non-compliant meal", explained as a series). */
  | { readonly chip: 'part_of_series'; readonly businessDate: BusinessDate }
  /** It feeds only a day to review ("Review", part of that day's shift). */
  | { readonly chip: 'review'; readonly businessDate: BusinessDate };

/**
 * A card's chip from its own verdict (`cardCausesMealViolation`) and every flagged day loaded (owed, owed late or review,
 * in range or not): the day it feeds is an owed one when it feeds several. Null when nothing applies.
 */
export function timecardMealChip(
  timecardId: TimecardId,
  causesViolation: boolean,
  days: readonly { readonly businessDate: BusinessDate; readonly state: MealPremiumState; readonly timecardIds: readonly TimecardId[]; readonly windowSaturated: boolean }[],
): TimecardMealChip | null {
  const feeds = days.filter((d) => d.state !== 'clear' && d.timecardIds.includes(timecardId));
  const saturatedDay = feeds.find((d) => d.windowSaturated);
  if (saturatedDay !== undefined) return { chip: 'cant_evaluate', businessDate: saturatedDay.businessDate };
  if (causesViolation) return { chip: 'causes_violation' };
  const day = feeds.find((d) => d.state === 'owed' || d.state === 'owed_late') ?? feeds[0];
  if (day === undefined) return null;
  return day.state === 'review' ? { chip: 'review', businessDate: day.businessDate } : { chip: 'part_of_series', businessDate: day.businessDate };
}

/** Legacy `GetEmployeeDetailAction::RECENT_TIMECARDS_WINDOW_DAYS`. */
export const DETAIL_TAB_WINDOW_DAYS = 31;
const DAY = 86_400_000n;

/** A card on the employee page's Timecards tab: open or closed, its breaks marked when their definition was deleted. */
export type DetailTabCard = {
  readonly timecardId: TimecardId;
  readonly clockedInAt: Instant;
  readonly clockedOutAt?: Instant;
  readonly breaks: readonly (TimecardBreak & { readonly definitionDeleted?: boolean })[];
};

/**
 * The employee page's Timecards tab while the location flags meal premiums (legacy `GetEmployeeDetailAction` with
 * `ca_meal_premium_flagging_enabled`): the cards clocked in from 31 days before the newest clock-in to a day after it,
 * newest first, each marked when it trips a violation on its own (open cards never do); and the flagged days of those
 * cards' closed ones (`premium_days`, the aggregator over that window as it stands: no widening, so a work period cut at
 * the window's far edge is judged on what is left, as legacy's tab judges it). A break whose definition was deleted is
 * neither a meal nor unconfirmable, as legacy's detector reads its missing definition (`break?->paid === false`), so
 * deleting a definition turns the meals taken under it into missed ones (B418, kept for parity; L579). Under
 * `decided_fixes` (switched off until parity is validated) such a break counts as the kind it was taken as.
 */
export function detailTabMealPremium(
  input: Omit<MealPremiumInput, 'cards' | 'includeClearDays' | 'window'> & { readonly cards: readonly DetailTabCard[] },
  arithmetic: DetailArithmetic = DEFAULT_DETAIL_ARITHMETIC,
): {
  readonly cards: readonly { readonly timecardId: TimecardId; readonly causesViolation: boolean }[];
  readonly days: readonly WorkdayMealPremium[];
} {
  if (input.cards.length === 0) return { cards: [], days: [] };
  const anchor = input.cards.reduce((max, c) => (c.clockedInAt > max ? c.clockedInAt : max), input.cards[0]?.clockedInAt ?? 0n);
  const from = anchor - BigInt(DETAIL_TAB_WINDOW_DAYS) * DAY;
  const to = anchor + DAY;
  const shown = input.cards
    .filter((c) => c.clockedInAt >= from && c.clockedInAt <= to)
    .sort((a, b) => (a.clockedInAt !== b.clockedInAt ? (a.clockedInAt > b.clockedInAt ? -1 : 1) : a.timecardId < b.timecardId ? -1 : a.timecardId > b.timecardId ? 1 : 0));
  const closedCards: MealPremiumCard[] = shown.flatMap((c) => (c.clockedOutAt === undefined ? [] : [{
    timecardId: c.timecardId,
    clockedInAt: c.clockedInAt,
    clockedOutAt: c.clockedOutAt,
    breaks: c.breaks,
  }]));
  const standing = { wage: input.wage, waivers: input.waivers, exempt: input.exempt, location: input.location, countDeletedDefinitions: arithmetic === 'decided_fixes' };
  const assessment = assessMealPremiums({ ...standing, cards: closedCards });
  const closedById = new Map(closedCards.map((c) => [c.timecardId, c] as const));
  return {
    cards: shown.map((c) => {
      const closed = closedById.get(c.timecardId);
      return { timecardId: c.timecardId, causesViolation: closed !== undefined && cardCausesMealViolation(closed, standing) };
    }),
    days: assessment.days.filter((d) => d.state !== 'clear'),
  };
}
